import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchCurrentUser = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/auth/me`, {
        withCredentials: true,
        timeout: 10000,
      });
      setUser(response.data);
    } catch (error) {
      const status = error?.response?.status;
      if (status === 401 || status === 403) {
        setUser(null);
      } else {
        // Retry once for transient errors
        try {
          await new Promise(r => setTimeout(r, 2000));
          const retry = await axios.get(`${API_URL}/auth/me`, {
            withCredentials: true,
            timeout: 10000,
          });
          setUser(retry.data);
        } catch (retryErr) {
          const retryStatus = retryErr?.response?.status;
          if (retryStatus === 401 || retryStatus === 403) {
            setUser(null);
          }
        }
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCurrentUser();
  }, [fetchCurrentUser]);

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/auth/login`, { email, password }, { withCredentials: true });
    const { token: newToken, user: userData, requires_2fa } = response.data;
    
    if (requires_2fa) {
      return { requires_2fa: true, tempToken: newToken };
    }
    
    // Cookie is set by backend. Store token in localStorage as fallback only.
    if (newToken) localStorage.setItem('token', newToken);
    setUser(userData);
    return { requires_2fa: false, user: userData };
  };

  const complete2FA = async (tempToken, code) => {
    const response = await axios.post(`${API_URL}/auth/2fa/verify`, { token: tempToken, code }, { withCredentials: true });
    const { token: fullToken, user: userData } = response.data;
    if (fullToken) localStorage.setItem('token', fullToken);
    setUser(userData);
    return userData;
  };

  const complete2FABackup = async (tempToken, backupCode) => {
    const response = await axios.post(`${API_URL}/auth/2fa/verify-backup`, { token: tempToken, backup_code: backupCode }, { withCredentials: true });
    const { token: fullToken, user: userData } = response.data;
    if (fullToken) localStorage.setItem('token', fullToken);
    setUser(userData);
    return userData;
  };

  const register = async (data) => {
    const response = await axios.post(`${API_URL}/auth/register`, data, { withCredentials: true });
    const { token: newToken, user: userData, checkout_url } = response.data;
    if (newToken) localStorage.setItem('token', newToken);
    setUser(userData);
    return { user: userData, checkout_url };
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/auth/logout`, {}, { withCredentials: true });
    } catch (err) {
      console.error('Logout request failed:', err);
    }
    localStorage.removeItem('token');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, loading, complete2FA, complete2FABackup }}>
      {children}
    </AuthContext.Provider>
  );
};
